import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Avatar,
  Breadcrumbs,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Build as RevisionIcon,
  Devices as DeviceIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi, useAsyncOperation } from '../../../hooks/useApi';
import { apiService } from '../../../services/api';
import { User } from '../../../types';
import { getDeviceDisplayName, getDeviceSubtitle } from '../../../utils/helpers';

interface CreateDeviceRevisionProps {}

const CreateDeviceRevision: React.FC<CreateDeviceRevisionProps> = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();

  const [revision, setRevision] = useState({
    reason: '',
    priority: 'medium',
    notes: '',
    technicianId: '',
    estimatedCompletionDate: '',
  });

  // API calls
  const {
    data: device,
    loading: deviceLoading,
    error: deviceError,
    execute: fetchDevice,
  } = useApi(() => deviceId ? apiService.getDevice(deviceId) : Promise.reject('No device ID'));

  const {
    data: technicians,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians());

  const { execute: createRevision, loading: creating } = useAsyncOperation();

  useEffect(() => {
    if (deviceId) {
      fetchDevice();
      fetchTechnicians();
    }
  }, [deviceId]);

  const handleCreateRevision = async () => {
    if (!deviceId || !revision.reason) {
      return;
    }

    const result = await createRevision(() => apiService.createDeviceRevision({
      deviceId,
      reason: revision.reason as any,
      priority: revision.priority as any,
      notes: revision.notes,
      technicianId: revision.technicianId || undefined,
      estimatedCompletionDate: revision.estimatedCompletionDate || undefined,
    }));

    if (result) {
      navigate('/devices/revisions');
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'maintenance': return 'Mantenimiento';
      case 'client_request': return 'Solicitud del Cliente';
      case 'quality_check': return 'Control de Calidad';
      case 'malfunction': return 'Mal Funcionamiento';
      default: return reason;
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return priority;
    }
  };

  if (deviceLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (deviceError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error cargando dispositivo: {deviceError}
        <Button onClick={() => fetchDevice()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  if (!device) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Dispositivo no encontrado
      </Alert>
    );
  }

  // Check if device is already in revision
  if (device.status === 'revision' || device.revisionInfo?.isInRevision) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/devices')}
          >
            Dispositivos
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/devices/${deviceId}`)}
          >
            {device.name || device.imei}
          </Link>
          <Typography variant="body2" color="text.primary">
            Revisión
          </Typography>
        </Breadcrumbs>

        <Alert severity="warning" sx={{ mt: 2 }}>
          Este dispositivo ya está en revisión.
          <Button
            onClick={() => navigate(`/devices/${deviceId}/revision-history`)}
            sx={{ ml: 2 }}
          >
            Ver Historial
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/devices')}
          >
            Dispositivos
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/devices/${deviceId}`)}
          >
            {device.name || device.imei}
          </Link>
          <Typography variant="body2" color="text.primary">
            Nueva Revisión
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Crear Revisión
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Programa una revisión para el dispositivo {device.name || device.imei}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(`/devices/${deviceId}`)}
          >
            Volver
          </Button>
        </Box>
      </Box>

      {/* Device Info */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
              <DeviceIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {getDeviceDisplayName(device)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getDeviceSubtitle(device) || `IMEI: ${device.imei}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estado: {device.status}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Create Revision Form */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Detalles de la Revisión
          </Typography>

          <Stack spacing={3}>
            <FormControl fullWidth required>
              <InputLabel>Razón de la Revisión</InputLabel>
              <Select
                value={revision.reason}
                label="Razón de la Revisión"
                onChange={(e) => setRevision({ ...revision, reason: e.target.value })}
              >
                <MenuItem value="maintenance">Mantenimiento Programado</MenuItem>
                <MenuItem value="client_request">Solicitud del Cliente</MenuItem>
                <MenuItem value="quality_check">Control de Calidad</MenuItem>
                <MenuItem value="malfunction">Mal Funcionamiento Reportado</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Prioridad</InputLabel>
              <Select
                value={revision.priority}
                label="Prioridad"
                onChange={(e) => setRevision({ ...revision, priority: e.target.value })}
              >
                <MenuItem value="low">Baja</MenuItem>
                <MenuItem value="medium">Media</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Asignar Técnico (Opcional)</InputLabel>
              <Select
                value={revision.technicianId}
                label="Asignar Técnico (Opcional)"
                onChange={(e) => setRevision({ ...revision, technicianId: e.target.value })}
              >
                <MenuItem value="">Sin asignar</MenuItem>
                {(technicians as any)?.data?.map((tech: User) => (
                  <MenuItem key={tech._id} value={tech._id}>
                    {tech.firstName && tech.lastName ? `${tech.firstName} ${tech.lastName}` : tech.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Fecha Estimada de Finalización (Opcional)"
              type="date"
              value={revision.estimatedCompletionDate}
              onChange={(e) => setRevision({ ...revision, estimatedCompletionDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Deja vacío si no hay una fecha estimada"
            />

            <TextField
              fullWidth
              label="Descripción y Notas"
              multiline
              rows={4}
              value={revision.notes}
              onChange={(e) => setRevision({ ...revision, notes: e.target.value })}
              placeholder="Describe el problema, síntomas observados, o detalles específicos de la revisión..."
              helperText="Proporciona todos los detalles que puedan ayudar al técnico"
            />

            {/* Preview */}
            {revision.reason && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Resumen de la Revisión:
                </Typography>
                <Typography variant="body2">
                  • Razón: {getReasonLabel(revision.reason)}
                </Typography>
                <Typography variant="body2">
                  • Prioridad: {getPriorityLabel(revision.priority)}
                </Typography>
                {revision.technicianId && (
                  <Typography variant="body2">
                    • Técnico asignado: {(() => {
                      const tech = (technicians as any)?.data?.find((t: User) => t._id === revision.technicianId);
                      return tech?.firstName && tech?.lastName ? `${tech.firstName} ${tech.lastName}` : tech?.name;
                    })()}
                  </Typography>
                )}
                {revision.estimatedCompletionDate && (
                  <Typography variant="body2">
                    • Fecha estimada: {new Date(revision.estimatedCompletionDate).toLocaleDateString('es-DO')}
                  </Typography>
                )}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/devices/${deviceId}`)}
                disabled={creating}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleCreateRevision}
                disabled={!revision.reason || creating}
              >
                {creating ? 'Creando...' : 'Crear Revisión'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateDeviceRevision;